<script lang="ts">
import type { FileInfoInput } from '#layers/assets/composables/use-file-info';
import type { GlobalFileId } from '#layers/data-model';

export interface RenderAssetImageFileInput extends FileInfoInput {
	id: GlobalFileId;
}

export interface RenderAssetImageProps {
	/**
	 * The file to render. Can be a file object or just a file ID string.
	 */
	file: RenderAssetImageFileInput | GlobalFileId;
	/**
	 * Indicate that this is a project image and should be prefixed with the project slug.
	 * Set to false for system-level assets.
	 */
	project?: boolean;
}
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useProjectStore } from '#imports';
import { useFileInfo } from '#layers/assets/composables/use-file-info';

const props = withDefaults(
	defineProps<RenderAssetImageProps>(),
	{ project: true },
);

const { currentProjectSlug } = useProjectStore();

const fileId = computed(() => typeof props.file === 'string' ? props.file : props.file.id);
const fileObject = computed(() => typeof props.file === 'string' ? null : props.file);

const { title } = useFileInfo(fileObject);
const hasError = ref(false);

watch(() => props.file, () => {
	hasError.value = false;
});
</script>

<template>
	<NuxtImg
		v-if="!hasError"
		:src="fileId"
		:alt="title"
		:modifiers="{ project: project ? currentProjectSlug : undefined }"
		v-bind="$attrs"
		@load="hasError = false"
		@error="hasError = true"
	/>
	<slot v-else name="error">
		<DynamicLogo
			v-bind="$attrs"
		/>
	</slot>
</template>
